<template>
  <LoadingSpinner :loading="loading"> </LoadingSpinner>

  <div class="container mt-4 mb-5">
    <div class="d-flex justify-content-between align-items-start flex-wrap" style="gap:1rem">
      <div>
        <button class="mdm-back" @click="$router.push('/mdm')">
          <font-awesome-icon :icon="['fas', 'arrow-left']" /> CAGISTA MDM
        </button>
        <h3 class="mb-1 mt-1">Enroll enhet</h3>
        <div style="font-style: italic; color:var(--bo-muted); max-width:680px">
          Generer en enrollment-token for en policy + merchant. Enheten kan enrolles med QR-kode
          (krever kamera) eller ved å skrive inn tokenet manuelt — begge fremgangsmåtene vises
          sammen med resultatet.
        </div>
        <!-- A token here enrols a device into the PRODUCTION fleet: the enterprise is shared, and the
             policy it lands on is a real one. -->
        <div v-if="isLocalDev()" class="mdm-devbanner mt-2">
          <font-awesome-icon :icon="['fas', 'lock']" />
          <span>
            Kjører fra <code>localhost</code> mot samme AMAPI-enterprise som produksjon. Enrollment er
            <strong>slått av</strong> her: et token herfra melder enheten inn i produksjonsflåten, på en
            ekte policy. Bruk produksjons-Backoffice.
          </span>
        </div>
      </div>
      <span v-if="status && status.configured" class="mdm-enterprise">
        Enterprise <code>{{ status.enterpriseId }}</code>
      </span>
    </div>

    <div v-if="!loading && status && !status.configured" class="alert alert-warning mt-4">
      <strong>Android Management API er ikke konfigurert.</strong>
      Se <code>CagistaServices/docs/amapi-setup-runbook.md</code>.
    </div>

    <template v-if="!loading && status && status.configured">
      <div class="row g-3 mt-1" style="max-width:900px">
        <div class="col-md-3">
          <label class="form-label mb-1 mdm-label">Policy</label>
          <select class="form-select" v-model="enroll.policyId">
            <option v-for="opt in policyChoices" :key="opt.name" :value="opt.name" :disabled="!opt.inAmapi">
              {{ opt.name }}{{ opt.inAmapi ? '' : ' — ikke lagret til AMAPI' }}
            </option>
          </select>
          <div v-if="policiesMissingFromAmapi.length" style="color:#c0392b; font-size:0.8rem" class="mt-1">
            {{ policiesMissingFromAmapi.join(', ') }} finnes i policy-innstillingene, men er ikke lagret til
            AMAPI ennå, og kan derfor ikke enrolles. Kjør «Lagre og oppdater policyer» på «Policyer»-siden.
          </div>
          <div v-if="!policyChoices.length" style="color:#c0392b; font-size:0.8rem" class="mt-1">
            Ingen policyer ennå — opprett en policy først på «Policyer».
          </div>
        </div>
        <div class="col-md-6 enroll-merchant">
          <label class="form-label mb-1 mdm-label">Merchant <span class="text-danger">*</span></label>
          <input class="form-control" type="text" v-model="merchantSearch" autocomplete="off"
                 placeholder="Søk navn eller slug…"
                 @focus="merchantDropdownOpen = true"
                 @input="onMerchantInput"
                 @blur="closeMerchantDropdownSoon" />
          <div v-if="merchantDropdownOpen" class="enroll-merchant__dropdown">
            <div v-for="m in filteredEnrollMerchants" :key="m.merchant"
                 class="enroll-merchant__item" @mousedown.prevent="selectEnrollMerchant(m)">
              <span class="enroll-merchant__name">{{ m.name }}</span>
              <code>{{ m.merchant }}</code>
            </div>
            <div v-if="!filteredEnrollMerchants.length" class="enroll-merchant__empty">Ingen treff</div>
          </div>
          <!-- Full selected merchant (the input truncates in this narrow column; this wraps and shows all text). -->
          <div v-if="enroll.merchant" class="enroll-merchant__selected mt-1">
            Valgt: <strong>{{ selectedMerchantName }}</strong> <code>{{ enroll.merchant }}</code>
          </div>
        </div>
        <div class="col-md-3">
          <label class="form-label mb-1 mdm-label">Gyldig i</label>
          <select class="form-select" v-model.number="enroll.durationSeconds">
            <option :value="3600">1 time</option>
            <option :value="86400">1 dag</option>
            <option :value="604800">7 dager</option>
          </select>
        </div>
      </div>
      <button class="bo_btn mt-3" @click="generateEnrollment"
              :disabled="enrolling || !enroll.policyId || !enroll.merchant || isLocalDev()"
              :title="isLocalDev() ? 'Skrivebeskyttet fra localhost — bruk produksjons-Backoffice' : ''">
        {{ enrolling ? 'Genererer…' : 'Generer enrollment-QR' }}
      </button>
      <p v-if="!enroll.merchant" class="mt-2 mb-0" style="color:var(--bo-muted); font-size:0.85rem">Velg en merchant for å generere QR.</p>
      <p v-if="enrollError" class="text-danger mt-2">{{ enrollError }}</p>

      <div v-if="enrollResult" class="mt-3 p-3 mdm-qr-card">
        <div class="enroll-methods">
          <!-- A. Enhet MED kamera -->
          <div class="enroll-method">
            <div class="enroll-method__title">Med kamera</div>
            <img v-if="qrDataUrl" :src="qrDataUrl" alt="Enrollment QR" style="width:260px; height:260px" />
            <ol class="enroll-steps">
              <li>Fabrikknullstill enheten</li>
              <li>Tapp <strong>6×</strong> på velkomstskjermen</li>
              <li>Skann QR-koden</li>
            </ol>
          </div>

          <!-- B. Enhet UTEN kamera (mange POS/KIOSK/KDS) -->
          <div class="enroll-method">
            <div class="enroll-method__title">Uten kamera <span class="enroll-method__hint">(POS/KIOSK/KDS uten kamera)</span></div>
            <ol class="enroll-steps">
              <li>Fabrikknullstill enheten og start oppsettveiviseren</li>
              <li><strong>Koble til Wi-Fi først</strong> — Android Device Policy må lastes ned</li>
              <li>På Google-innloggingsskjermen: skriv <code>afw#setup</code> i e-postfeltet
                  (i stedet for en e-postadresse)</li>
              <li>Godta nedlasting og installasjon av Android Device Policy</li>
              <li>Skriv inn tokenet under når enheten ber om det</li>
            </ol>
            <div class="enroll-token">
              <code class="enroll-token__value">{{ enrollResult.value }}</code>
              <button class="bo_btn_secondary enroll-token__copy" @click="copyToken">
                {{ tokenCopied ? "Kopiert" : "Kopier" }}
              </button>
            </div>
            <div class="enroll-token__note">
              Enheten må være fabrikknullstilt <strong>uten Google-konto</strong> — ellers tilbys ikke
              device-owner-oppsett.
            </div>
          </div>
        </div>

        <div class="enroll-expiry">Utløper: {{ formatTime(enrollResult.expirationTimestamp) }}</div>
      </div>
    </template>
  </div>

  <ConfirmDialogue ref="confirmDialog" />
</template>

<script>
import LoadingSpinner from "@/components/other/LoadingSpinner";
import ConfirmDialogue from "@/components/other/ConfirmDialogue";
import MdmService from "@/services/system/MdmService";
import { isLocalDev, refuseIfLocalDev } from "@/util/MdmLocalGuard";
import MerchantService from "@/services/merchant/MerchantService";
import QRCode from "qrcode";

export default {
  name: "MdmEnroll",
  components: { LoadingSpinner, ConfirmDialogue },
  data() {
    return {
      loading: true,
      status: null,
      policies: [],
      policyKinds: {},   // name -> kind (MULTI_APP | SINGLE_APP | MAINTENANCE); excludes maintenance from enroll
      enroll: { policyId: "", merchant: "", durationSeconds: 3600 },
      enrolling: false,
      enrollError: "",
      enrollResult: null,
      qrDataUrl: "",
      tokenCopied: false,
      tokenCopyTimer: null,
      merchants: [],
      merchantSearch: "",
      merchantDropdownOpen: false,
      merchantBlurTimer: null
    };
  },
  computed: {
    /** The policy names AMAPI actually holds — only these can be enrolled into. */
    amapiPolicyNames() {
      return new Set(this.policies.map((p) => this.shortName(p.name, "policies")).filter(Boolean));
    },
    /**
     * Every policy a device could be enrolled into, from the POLICY SETTINGS rather than from AMAPI,
     * each marked with whether AMAPI has it yet.
     *
     * <p>It used to list AMAPI only, so a policy that existed in the settings but had never been pushed
     * was simply ABSENT from this dropdown — with nothing to say why. That is how POS_DESKTOP and
     * POS_DESKTOP_PILOT could be configured, visible on «Policyer», and impossible to enrol: the console
     * showed KDS, KIOSK and KIOSK_PILOT and gave no hint that anything was missing.</p>
     *
     * <p>Still excluded, because none of them is a thing you enrol INTO:</p>
     *   MAINTENANCE kind — the unlocked "exit kiosk" variant, excluded by KIND rather than by name;
     *   *_PROV   — enrolling on the steady policy auto-redirects there;
     *   *_UPDATE — a copy with one app deliberately OMITTED so ADP uninstalls it; enrolling onto one
     *              would bring the device up without that app. Leftovers from a finished «Tving
     *              oppdatering», which started showing up once forced updates were in regular use.
     */
    policyChoices() {
      return Object.keys(this.policyKinds || {})
        .filter((p) => !p.endsWith("_PROV") && !p.endsWith("_UPDATE") && this.policyKinds[p] !== "MAINTENANCE")
        .sort()
        .map((name) => ({ name, inAmapi: this.amapiPolicyNames.has(name) }));
    },
    /** The selectable ones — what a device can actually be enrolled into right now. */
    policyOptions() {
      return this.policyChoices.filter((c) => c.inAmapi).map((c) => c.name);
    },
    /** Configured but never pushed: shown greyed out, with the fix named. */
    policiesMissingFromAmapi() {
      return this.policyChoices.filter((c) => !c.inAmapi).map((c) => c.name);
    },
    selectedMerchantName() {
      const m = this.merchants.find((x) => x.merchant === this.enroll.merchant);
      return m ? m.name : "";
    },
    filteredEnrollMerchants() {
      const q = this.merchantSearch.trim().toLowerCase();
      const list = !q
        ? this.merchants
        : this.merchants.filter((m) =>
            [m.name, m.merchant].filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));
      return list.slice(0, 50);
    }
  },
  mounted() {
    MdmService.getStatus().then(
      (response) => {
        this.status = response.data;
        this.loading = false;
        if (this.status.configured) {
          this.loadPolicyKinds();
          this.loadMerchants();
        }
      },
      (error) => {
        console.log(error);
        this.status = { configured: false };
        this.loading = false;
      }
    );
  },
  methods: {
    shortName(name, segment) {
      if (!name) return "";
      const marker = "/" + segment + "/";
      const i = name.indexOf(marker);
      return i >= 0 ? name.substring(i + marker.length) : name;
    },
    formatTime(iso) {
      if (!iso) return "—";
      const dt = new Date(iso);
      if (isNaN(dt.getTime())) return iso;
      const p = (n) => String(n).padStart(2, "0");
      return p(dt.getDate()) + "." + p(dt.getMonth() + 1) + "." + dt.getFullYear() +
             " " + p(dt.getHours()) + ":" + p(dt.getMinutes());
    },
    // Load policy kinds first (so policyOptions can exclude MAINTENANCE-kind by kind), then the policies.
    loadPolicyKinds() {
      MdmService.getPolicySettings().then(
        (res) => { this.policyKinds = (res.data && res.data.kinds) || {}; this.loadPolicies(); },
        (error) => { console.log(error); this.loadPolicies(); }
      );
    },
    loadPolicies() {
      MdmService.listPolicies().then(
        (response) => {
          this.policies = Array.isArray(response.data.policies) ? response.data.policies : [];
          // Default to the first real policy (or keep the current one if it still exists).
          const opts = this.policyOptions;
          if (!this.enroll.policyId || !opts.includes(this.enroll.policyId)) {
            this.enroll.policyId = opts[0] || "";
          }
        },
        (error) => { console.log(error); }
      );
    },
    loadMerchants() {
      MerchantService.listAllMerchants().then(
        (response) => {
          const list = Array.isArray(response.data) ? response.data : [];
          this.merchants = list
            .filter((m) => m.merchant)
            .sort((a, b) => String(a.name || "").localeCompare(String(b.name || ""), "nb"));
        },
        (error) => { console.log(error); }
      );
    },
    onMerchantInput() {
      this.enroll.merchant = "";
      this.merchantDropdownOpen = true;
    },
    selectEnrollMerchant(m) {
      this.enroll.merchant = m.merchant;
      this.merchantSearch = `${m.name} (${m.merchant})`;
      this.merchantDropdownOpen = false;
    },
    closeMerchantDropdownSoon() {
      clearTimeout(this.merchantBlurTimer);
      this.merchantBlurTimer = setTimeout(() => { this.merchantDropdownOpen = false; }, 150);
    },
    // navigator.clipboard needs a secure context; keep the textarea fallback so it also works over plain http.
    copyToken() {
      const value = this.enrollResult ? this.enrollResult.value : "";
      if (!value) return;
      const done = () => {
        this.tokenCopied = true;
        clearTimeout(this.tokenCopyTimer);
        this.tokenCopyTimer = setTimeout(() => { this.tokenCopied = false; }, 2000);
      };
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(value).then(done, () => { this.copyTokenFallback(value, done); });
      } else {
        this.copyTokenFallback(value, done);
      }
    },
    copyTokenFallback(value, done) {
      const el = document.createElement("textarea");
      el.value = value;
      el.setAttribute("readonly", "");
      el.style.position = "absolute";
      el.style.left = "-9999px";
      document.body.appendChild(el);
      el.select();
      try { document.execCommand("copy"); done(); } catch (e) { console.log(e); }
      document.body.removeChild(el);
    },
    isLocalDev,
    refuseIfLocalDev() {
      return refuseIfLocalDev(this.$refs.confirmDialog,
        "Et enrollment-token herfra melder enheten inn i produksjonsflåten, på en ekte policy.");
    },
    async generateEnrollment() {
      if (await this.refuseIfLocalDev()) return;
      this.enrolling = true;
      this.enrollError = "";
      this.enrollResult = null;
      this.qrDataUrl = "";
      this.tokenCopied = false;
      MdmService.createEnrollmentToken(this.enroll.policyId, this.enroll.durationSeconds, this.enroll.merchant).then(
        (response) => {
          this.enrolling = false;
          this.enrollResult = response.data;
          if (this.enrollResult.qrCode) {
            QRCode.toDataURL(this.enrollResult.qrCode, { width: 260, margin: 1 })
              .then((url) => { this.qrDataUrl = url; })
              .catch((e) => { console.log(e); });
          }
        },
        (error) => {
          this.enrolling = false;
          this.enrollError = this.errMsg(error, "Kunne ikke generere enrollment-token.");
        }
      );
    },
    errMsg(error, fallback) {
      const data = error && error.response ? error.response.data : null;
      return (data && data.error) || fallback;
    }
  }
};
</script>

<style scoped>
/* Localhost-against-production notice, same as the other MDM pages */
.mdm-devbanner {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  max-width: 680px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 0.84rem;
  font-style: normal;
  color: var(--bo-brand-ink);
  background: var(--bo-brand-tint);
  border: 1px solid #ffd591;
}

.mdm-back {
  border: none;
  background: none;
  color: var(--bo-brand-ink);
  padding: 0;
  font-size: 0.9rem;
  cursor: pointer;
}
.mdm-back:hover {
  text-decoration: underline;
}
.mdm-enterprise {
  color: var(--bo-muted);
  font-size: 0.9rem;
  white-space: nowrap;
}
.mdm-label {
  font-size: 0.85rem;
  color: var(--bo-muted);
}
.mdm-qr-card {
  border: 1px solid var(--bo-line);
  border-radius: 10px;
  width: fit-content;
  max-width: 860px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
}

/* The two enrollment paths side by side on desktop, stacked on mobile. */
.enroll-methods {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
}
.enroll-method {
  flex: 1 1 300px;
  min-width: 0;
}
.enroll-method__title {
  font-weight: 600;
  margin-bottom: 0.5rem;
}
.enroll-method__hint {
  font-weight: 400;
  color: var(--bo-muted);
  font-size: 0.85rem;
}
.enroll-steps {
  margin: 0.6rem 0 0;
  padding-left: 1.1rem;
  font-size: 0.88rem;
  line-height: 1.55;
}
.enroll-steps code {
  background: var(--bo-surface-2);
  padding: 1px 5px;
  border-radius: 4px;
  color: var(--bo-brand-ink);
}
.enroll-token {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.6rem;
  margin-top: 0.8rem;
}
/* Big and widely spaced — this gets typed by hand on a device keyboard. */
.enroll-token__value {
  font-size: 1.15rem;
  letter-spacing: 0.14em;
  font-weight: 600;
  color: var(--bo-ink);
  background: var(--bo-surface-2);
  border: 1px solid #e3e3e3;
  border-radius: 6px;
  padding: 6px 10px;
  overflow-wrap: anywhere;
}
.enroll-token__copy {
  min-width: 90px;
}
.enroll-token__note {
  margin-top: 0.5rem;
  font-size: 0.82rem;
  color: var(--bo-muted);
}
.enroll-expiry {
  margin-top: 1rem;
  padding-top: 0.7rem;
  border-top: 1px solid var(--bo-surface-2);
  color: var(--bo-muted);
  font-size: 0.85rem;
}

.enroll-merchant {
  position: relative;
}
.enroll-merchant__dropdown {
  position: absolute;
  z-index: 20;
  left: 0;
  right: auto;
  /* Grow past the narrow column to fit the widest name + slug on one line (capped, never past the viewport). */
  min-width: 100%;
  width: max-content;
  max-width: min(460px, 92vw);
  margin-top: 2px;
  background: var(--bo-surface);
  border: 1px solid var(--bo-line);
  border-radius: 8px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.1);
  max-height: 260px;
  overflow-y: auto;
}
.enroll-merchant__item {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.45rem 0.7rem;
  cursor: pointer;
  border-bottom: 1px solid #f3f3f3;
}
.enroll-merchant__item:last-child {
  border-bottom: none;
}
.enroll-merchant__item:hover {
  background: var(--bo-surface-2);
}
.enroll-merchant__name {
  font-weight: 500;
}
.enroll-merchant__item code {
  color: var(--bo-muted);
  font-size: 0.82rem;
  white-space: nowrap;
}
.enroll-merchant__empty {
  padding: 0.5rem 0.7rem;
  color: var(--bo-muted);
}
.enroll-merchant__selected {
  font-size: 0.85rem;
  color: var(--bo-ink);
  overflow-wrap: anywhere;
}
.enroll-merchant__selected code {
  color: var(--bo-muted);
}
</style>
